import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../app/store";
import {
    BotInListDTO,
    CcRecipientDTO,
    LocaleTag,
    CreateBotRequest,
    SupportedTimezone
} from "../../../api/generated";
import {v4} from "uuid";
import {botApi, RequestStatus} from "../../../api/apiConfiguration";
import {selectExceptionally} from "../../../utils/selectExceptionally";
import {removeTrailingSlash} from "../../../utils/urlUtils";
import {DEFAULT_SUPPORTED_PHONE_NUMBER_LOCALES} from "./phoneLocaleUtil";
import {DEFAULT_SURVEY_MESSAGE_COUNT, normalizeSurveyMessageCount} from "./surveySettings";
import {FranchiseRequestUi} from "./franchiseTypes";

export const createBot = createAsyncThunk<BotInListDTO, undefined, { state: RootState }>(
    "botCreate/create",
    async (_payload, {getState, rejectWithValue}) => {
        const {botCreate} = getState();
        const {createBotRequest} = botCreate;
        const request = mapRequest(createBotRequest);
        try {
            return await botApi.createBot({createBotRequest: request});
        } catch (error: any) {
            const message = error?.response ? await error.response.text() : null;
            return rejectWithValue(message);
        }
    }
);

export type CreateBotRequestUI =
    Omit<CreateBotRequest, "emailTranscriptRecipients" | "publicFranchiseIdList" | "ccRecipients">
    & {
    emailTranscriptRecipients: string,
    ccRecipients: CcRecipientDTO[],
    franchiseList: FranchiseRequestUi[],
    useMultiAgent: boolean,
    externalCommunicationsDisabled: boolean
}
export type CreateBotRequestPartial = Partial<CreateBotRequestUI>

export interface CreateBotState {
    createBotRequest: CreateBotRequestPartial
    createBotRequestStatus: RequestStatus
    errorMessage: string | null
}

const buildDefaultCreateBotRequest = (): CreateBotRequestPartial => {
    return {
        publicId: v4(),
        lastRenewalDate: new Date().toISOString().split('T')[0],
        filtrationEnabled: true,
        transcriptSummaryEnabled: false,
        sharingLinkEnabled: false,
        surveyAfterMessagesEnabled: false,
        surveyAfterMessagesCount: DEFAULT_SURVEY_MESSAGE_COUNT,
        surveyAfterBookingEnabled: false,
        useMultiAgent: false,
        vehicleProviderWarningEmailEnabled: true,
        externalCommunicationsDisabled: false,
        urlProcessorEnabled: false,
        timezone: SupportedTimezone.EuropeLondon,
        defaultLocale: LocaleTag.EnGb,
        supportedPhoneNumberLocales: DEFAULT_SUPPORTED_PHONE_NUMBER_LOCALES,
        ccRecipients: [],
        franchiseList: [],
    }
}

const buildInitialState = (): CreateBotState => {
    return {
        createBotRequest: buildDefaultCreateBotRequest(),
        createBotRequestStatus: RequestStatus.NONE,
        errorMessage: null
    }
}

const initialState: CreateBotState = buildInitialState()

export const botCreateSlice = createSlice({
    name: 'botCreate',
    initialState,
    reducers: {
        clearCreateBotState: () => {
            return buildInitialState();
        },
        updateCreateBotRequest: (state, {payload}: PayloadAction<CreateBotRequestPartial>) => {
            state.createBotRequest = {
                ...state.createBotRequest,
                ...payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createBot.pending, (state) => {
            state.createBotRequestStatus = RequestStatus.PENDING;
            state.errorMessage = null;
        })
        builder.addCase(createBot.fulfilled, (state) => {
            state.createBotRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(createBot.rejected, (state, action) => {
            state.createBotRequestStatus = RequestStatus.REJECTED;
            state.errorMessage = (action.payload as string | null) ?? null;
            state.createBotRequest.publicId = v4();
        })
    },
});

export const prepareEmails = (emailsInString?: string): Array<string> => {
    if (emailsInString) {
        return Array.from(new Set(selectExceptionally(emailsInString).split(";").map(e => e.trim())))
    }
    return [] as Array<string>
}

const mapFranchisePublicIdList = (franchiseList?: FranchiseRequestUi[]): string[] => {
    return (franchiseList || []).map(franchise => franchise.publicId);
}

const prepareCcRecipients = (recipients?: CcRecipientDTO[]): CcRecipientDTO[] => {
    const recipientsByEmail = new Map<string, CcRecipientDTO>();
    (recipients || [])
        .map(recipient => ({
            title: recipient.title?.trim() || "",
            email: recipient.email?.trim() || ""
        }))
        .filter(recipient => recipient.email)
        .forEach(recipient => {
            const key = recipient.email!.toLowerCase();
            if (!recipientsByEmail.has(key)) {
                recipientsByEmail.set(key, recipient);
            }
        });
    return Array.from(recipientsByEmail.values());
}

const prepareOptionalText = (value?: string): string | undefined => {
    const normalized = value?.trim();
    return normalized || undefined;
}

const mapRequest = (request: CreateBotRequestPartial): CreateBotRequest => {
    return {
        publicId: selectExceptionally(request.publicId),
        botType: selectExceptionally(request.botType),
        useMultiAgent: request.useMultiAgent ?? false,
        vehicleProviderWarningEmailEnabled: request.vehicleProviderWarningEmailEnabled ?? true,
        externalCommunicationsDisabled: request.externalCommunicationsDisabled ?? false,
        siteName: selectExceptionally(request.siteName),
        siteURL: removeTrailingSlash(selectExceptionally(request.siteURL)),
        groupHash: request.groupHash,
        publicFranchiseIdList: mapFranchisePublicIdList(request.franchiseList),
        conversationLimit: selectExceptionally(request.conversationLimit),
        workingRange: request.workingRange,
        teamInfo: request.teamInfo,
        openAiApiKey: request.openAiApiKey,
        openAiModel: prepareOptionalText(request.openAiModel),
        lastRenewalDate: selectExceptionally(request.lastRenewalDate),
        assistantEmail: request.assistantEmail,
        assistantEmailPassword: request.assistantEmailPassword,
        emailTranscriptRecipients: prepareEmails(request.emailTranscriptRecipients),
        filtrationEnabled: selectExceptionally(request.filtrationEnabled),
        transcriptSummaryEnabled: selectExceptionally(request.transcriptSummaryEnabled),
        sharingLinkEnabled: selectExceptionally(request.sharingLinkEnabled),
        surveyAfterMessagesEnabled: request.surveyAfterMessagesEnabled ?? false,
        surveyAfterMessagesCount: normalizeSurveyMessageCount(request.surveyAfterMessagesCount),
        surveyAfterBookingEnabled: request.surveyAfterBookingEnabled ?? false,
        redirectProvider: selectExceptionally(request.redirectProvider),
        urlProcessorEnabled: selectExceptionally(request.urlProcessorEnabled),
        matchField: request.matchField,
        matchType: request.matchType,
        segmentNumberFromTheEnd: request.segmentNumberFromTheEnd,
        timezone: request.timezone,
        defaultLocale: request.defaultLocale,
        supportedPhoneNumberLocales: request.supportedPhoneNumberLocales ?? DEFAULT_SUPPORTED_PHONE_NUMBER_LOCALES,
        ccRecipients: prepareCcRecipients(request.ccRecipients),
    } as CreateBotRequest & { externalCommunicationsDisabled: boolean }
}

export const {
    clearCreateBotState,
    updateCreateBotRequest
} = botCreateSlice.actions;

export const createBotRequestSelector = (state: RootState): CreateBotRequestPartial => state.botCreate.createBotRequest;
export const createBotRequestStatusSelector = (state: RootState): RequestStatus => state.botCreate.createBotRequestStatus;
export const createBotErrorMessageSelector = (state: RootState): string | null => state.botCreate.errorMessage;
